import { useEffect, useState } from 'react';
import { api } from '../api';

// AwardAlerts — set + manage award-availability alerts. User picks a
// route + cabin + thresholds; backend stores them. Today the "check
// now" button polls seats.aero on demand; a background cron job would
// auto-check periodically and surface hits as in-app + email/push.
//
// NOTE: This is the data layer + manual-check UI. Actual push/email
// notifications need a separate worker process (cron) to run hourly
// and POST a notification when last_hit changes. Tracked as future
// work — the UI here at least lets the user inspect on demand.
export default function AwardAlerts() {
  const [alerts, setAlerts] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ origin: 'JFK', destination: 'LIS', cabin: 'business', max_miles: 80000, max_taxes: 200, date_start: '', date_end: '' });
  const [checking, setChecking] = useState(null);
  const [results, setResults] = useState({});

  const reload = () => api.getAwardAlerts().then(r => setAlerts(r.alerts || []));
  useEffect(() => { reload(); }, []);

  const create = async () => {
    await api.createAwardAlert(form);
    setShowForm(false);
    reload();
  };
  const remove = async (id) => {
    await api.deleteAwardAlert(id);
    reload();
  };
  const check = async (id) => {
    setChecking(id);
    try {
      const r = await api.checkAwardAlert(id);
      setResults(prev => ({ ...prev, [id]: r }));
      reload();
    } finally {
      setChecking(null);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-3 sm:px-4 pb-20">
      <div className="text-center my-6">
        <div className="text-brand-ink/55 text-xs font-bold uppercase tracking-[0.3em]">AWARD ALERTS</div>
        <h1 className="text-2xl sm:text-3xl font-black mt-1 text-brand-ink">Watch for award space</h1>
        <p className="text-sm text-brand-ink/70 mt-1 max-w-lg mx-auto">
          Set a route + your max miles / max taxes. We watch seats.aero and surface hits.
          Doesn't book — just tells you when the deal is live so you can transfer + book yourself.
        </p>
      </div>

      <button
        onClick={() => setShowForm(true)}
        className="w-full bg-gradient-to-r from-teal to-gold text-navy font-bold py-3 rounded-xl mb-4"
      >
        + Set a new alert
      </button>

      {showForm && (
        <div className="bg-navy-light border border-teal/30 rounded-2xl p-4 mb-4 space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <Field label="From (IATA)" value={form.origin} onChange={v => setForm({ ...form, origin: v.toUpperCase() })} />
            <Field label="To (IATA)" value={form.destination} onChange={v => setForm({ ...form, destination: v.toUpperCase() })} />
            <div>
              <div className="text-[10px] font-bold text-brand-ink/65 uppercase mb-1">Cabin</div>
              <select value={form.cabin} onChange={e => setForm({ ...form, cabin: e.target.value })} className="w-full bg-brand-ink/5 border border-brand-ink/15 rounded-lg px-2 py-2 text-xs text-brand-ink">
                <option value="economy">Economy</option>
                <option value="premium">Premium economy</option>
                <option value="business">Business</option>
                <option value="first">First</option>
              </select>
            </div>
            <Field label="Max miles/pp" type="number" value={form.max_miles} onChange={v => setForm({ ...form, max_miles: Number(v) })} />
            <Field label="Max taxes ($)" type="number" value={form.max_taxes} onChange={v => setForm({ ...form, max_taxes: Number(v) })} />
            <Field label="Date start" type="date" value={form.date_start} onChange={v => setForm({ ...form, date_start: v })} />
            <Field label="Date end" type="date" value={form.date_end} onChange={v => setForm({ ...form, date_end: v })} />
          </div>
          <div className="flex gap-2">
            <button onClick={() => setShowForm(false)} className="px-4 py-2 bg-brand-ink/10 text-brand-ink/85 text-xs font-bold rounded-lg">Cancel</button>
            <button onClick={create} className="flex-1 bg-teal text-navy font-bold py-2 rounded-lg text-xs">Save alert</button>
          </div>
        </div>
      )}

      {alerts.length === 0 && !showForm && (
        <div className="text-center text-brand-ink/55 text-sm py-8">No alerts yet — tap above to add one.</div>
      )}

      <div className="space-y-2">
        {alerts.map(a => {
          const r = results[a.id];
          return (
            <div key={a.id} className="bg-navy-light border border-brand-ink/15 rounded-xl p-3">
              <div className="flex items-baseline justify-between gap-2 flex-wrap">
                <div>
                  <div className="font-bold text-brand-ink">{a.origin} → {a.destination}</div>
                  <div className="text-[10px] text-brand-ink/65">{a.cabin} · ≤{(a.max_miles || 0).toLocaleString()} miles · ≤${a.max_taxes || 0} tax · {a.date_start?.slice(0,10) || 'any'} → {a.date_end?.slice(0,10) || 'any'}</div>
                </div>
                <div className="flex gap-1.5">
                  <button onClick={() => check(a.id)} disabled={checking === a.id} className="text-[10px] font-bold bg-teal/15 text-teal hover:bg-teal hover:text-navy px-2 py-1 rounded transition disabled:opacity-40">
                    {checking === a.id ? 'Checking…' : 'Check now'}
                  </button>
                  <button onClick={() => remove(a.id)} className="text-[10px] font-bold bg-brand-ink/10 text-brand-ink/55 hover:text-rose-300 px-2 py-1 rounded">Remove</button>
                </div>
              </div>
              {(r?.hit || a.last_hit) && (
                <div className="mt-2 pt-2 border-t border-teal/30 text-[11px] text-brand-ink/85">
                  <strong className="text-teal">✓ Hit:</strong> {(r?.hit || a.last_hit).source} · {(r?.hit || a.last_hit).miles_per_person.toLocaleString()} mi · ${(r?.hit || a.last_hit).taxes_total} · {(r?.hit || a.last_hit).date}
                </div>
              )}
              {r && r.hit === null && (
                <div className="mt-2 pt-2 border-t border-brand-ink/15 text-[11px] text-brand-ink/55">
                  No options under your thresholds right now — keep watching.
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = 'text' }) {
  return (
    <div>
      <div className="text-[10px] font-bold text-brand-ink/65 uppercase mb-1">{label}</div>
      <input
        type={type}
        value={value}
        onChange={e => onChange(e.target.value)}
        className="w-full bg-brand-ink/5 border border-brand-ink/15 rounded-lg px-2 py-2 text-xs text-brand-ink"
      />
    </div>
  );
}
